import React, { useEffect, useCallback, useState } from "react"
import { AsyncSelectProps } from "./async-select"
import { ParagraphSmall } from "components/text"
import { CreatePharmacyParams, PharmacyExternalTypeEnum } from "typings/types"
import config from "config"

type GeocoderAddressComponent = google.maps.GeocoderAddressComponent
type GooglePlaceResult = google.maps.places.PlaceResult
type GetPlacesDetails = (placeId: string) => Promise<GooglePlaceResult>

export const useGooglePlaces = () => {
  const [autocompleteService, setAutocompleteService] = useState<google.maps.places.AutocompleteService>()
  const [placesService, setPlacesService] = useState<google.maps.places.PlacesService>()
  const loadSuggestions: AsyncSelectProps["loadOptions"] = useCallback(
    (inputValue, callback) => {
      if (inputValue && autocompleteService) {
        autocompleteService.getPlacePredictions(
          {
            input: inputValue,
            componentRestrictions: { country: "us" },
            types: ["establishment"]
          },
          results => {
            if (results) {
              const pharmacyOptions = results.map(option => {
                const label = (
                  <>
                    <ParagraphSmall display="inline" bold>
                      {option.structured_formatting.main_text},{" "}
                    </ParagraphSmall>
                    <ParagraphSmall display="inline">{option.structured_formatting.secondary_text}</ParagraphSmall>
                  </>
                )

                return {
                  value: option.place_id,
                  label
                }
              })

              callback(pharmacyOptions as any)
            }
          }
        )
      }
    },
    [autocompleteService]
  )
  const getPlaceDetails: GetPlacesDetails = async placeId => {
    if (!placesService) {
      throw new Error("Google places api has not yet loaded")
    } else {
      return new Promise((resolve, reject) => {
        placesService.getDetails({ placeId, fields: ["ALL"] }, (result, status) => {
          if (status === "OK") {
            resolve(result)
          } else {
            reject(status)
          }
        })
      })
    }
  }

  useEffect(() => {
    const onLoadGoogleMapsApi = () => {
      const {
        places: { AutocompleteService, PlacesService }
      } = window?.google.maps
      setAutocompleteService(new AutocompleteService())
      setPlacesService(new PlacesService(document.createElement("div")))
    }

    // if api already available
    if (window?.google?.maps) {
      onLoadGoogleMapsApi()
      return
    }

    const SCRIPT_ID = "google-maps-api"

    // if this is the first time it's being initialized
    if (!document.getElementById(SCRIPT_ID)) {
      // @ts-ignore
      window.onLoadGoogleMapsApi = onLoadGoogleMapsApi
      const script = document.createElement("script")
      script.src = `https://maps.googleapis.com/maps/api/js?key=${config.integrations.google.maps}&libraries=places&callback=onLoadGoogleMapsApi`
      script.id = SCRIPT_ID
      script.async = true
      document.head.appendChild(script)
      return () => {
        clearInterval(interval)
      }
    }

    // the script tag is initialized but hasn't loaded yet so wait for it
    const interval = setInterval(() => {
      if (window?.google?.maps) {
        onLoadGoogleMapsApi()
        clearInterval(interval)
      }
    }, 100)
    return () => {
      clearInterval(interval)
    }
  }, [])

  return { loadSuggestions, getPlaceDetails }
}

const getValueFromAddressComponent = (components: GeocoderAddressComponent[], type: string): string | undefined => {
  const component = components.find(component => component.types.includes(type))

  return component?.short_name
}

export const preparePharmacyParams = (placesQueryResult: GooglePlaceResult): CreatePharmacyParams => {
  const {
    address_components: addressComponents,
    place_id: placeId,
    name,
    formatted_phone_number: phoneNumber
  } = placesQueryResult

  if (!placeId || !addressComponents) {
    throw new Error("Could not find specified address in Google Places")
  }

  const state = getValueFromAddressComponent(addressComponents, "administrative_area_level_1")
  const zipCode = getValueFromAddressComponent(addressComponents, "postal_code")
  const city =
    getValueFromAddressComponent(addressComponents, "locality") ||
    getValueFromAddressComponent(addressComponents, "sublocality")

  const route = getValueFromAddressComponent(addressComponents, "route")
  const streetNumber = getValueFromAddressComponent(addressComponents, "street_number")
  const streetAddress1 = [route, streetNumber].join(" ")

  return {
    externalId: placeId,
    externalType: PharmacyExternalTypeEnum.Google,
    name,
    phoneNumber,
    addressAttributes: {
      streetAddress1,
      city,
      state,
      zipCode
    }
  }
}
